import { useSelector } from "react-redux";
import type {
  BookedFlightItinerary,
  FlightItinerarySegment,
  AirBookingFlightItinerarySlice,
  ScheduleChangeSeverity,
} from "@b2bportal/air-booking-api";
import type { TripSegment, TripSlice } from "@b2bportal/air-shopping-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  ScheduleChangeDisplayMode,
  type SliceChangeContext,
  SliceDirection,
} from "@b2bportal/core-types";
import { getSegmentScheduleChangeKey } from "@b2bportal/core-utilities";
import { Trans } from "@hopper-b2b/i18n";
import {
  FlightDetailsSegment,
  FlightItineraryRestrictionsDetails,
} from "@tenant/flights";
import clsx from "clsx";
import dayjs from "dayjs";

import type { AirlineMap, AirportMap } from "../../../../types";
import { getAirlines, getAirports } from "../../store/selectors";
import defaultStyles from "./FlightItinerarySliceDetails.module.scss";

export interface FlightItinerarySliceDetailsProps {
  itinerary: BookedFlightItinerary;
  slice: AirBookingFlightItinerarySlice;
  direction: SliceDirection;
  formattedDepartureDate: string;
  destination: string;
  className?: string;
  scheduleChangeDisplayMode?: ScheduleChangeDisplayMode;
  sliceChangeContext?: SliceChangeContext;
}

export const FlightItinerarySliceDetails = ({
  itinerary,
  slice,
  className,
  direction,
  formattedDepartureDate,
  destination,
  sliceChangeContext,
  scheduleChangeDisplayMode = ScheduleChangeDisplayMode.original,
}: FlightItinerarySliceDetailsProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FlightItinerarySliceDetails;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const airlines = useSelector(getAirlines);
  const airports = useSelector(getAirports);
  const tripSlice = transformToTripSlice(
    slice,
    direction === SliceDirection.Outbound,
    airlines,
    airports
  );

  return (
    <div className={clsx(block, className)}>
      <div className={cn("slice-label")}>
        <SliceLabel
          date={formattedDepartureDate}
          destination={destination}
          destinationClassName={cn("slice-destination")}
          direction={direction}
        />
      </div>
      <div className={cn("segments-list")}>
        {slice.segments.map((segment, index, allSegments) => {
          const segmentChangeKey = getSegmentScheduleChangeKey(segment);
          const updatedSegment = sliceChangeContext?.changes[segmentChangeKey];

          return (
            <FlightSegment
              key={`${index}-${segment.origin.locationCode}@${segment.scheduledDeparture}}-${segment.destination.locationCode}@${segment.scheduledArrival}`}
              slice={tripSlice}
              nextSegment={allSegments[index + 1]}
              scheduleChangeDisplayMode={scheduleChangeDisplayMode}
              scheduleChangeSeverity={sliceChangeContext?.severity}
              segment={segment}
              updatedSegment={updatedSegment}
            />
          );
        })}
      </div>
      <FlightItineraryRestrictionsDetails
        className={cn("restrictions-list")}
        itinerary={itinerary}
        slice={slice}
      />
    </div>
  );
};

const SliceLabel = ({
  date,
  destination,
  direction,
  destinationClassName,
}: {
  date: string;
  destination: string;
  destinationClassName: string;
  direction: SliceDirection;
}) => {
  switch (direction) {
    case SliceDirection.Outbound:
      return (
        <Trans
          i18nKey={"core-flights.itinerary.card.slice.outboundTitle"}
          values={{
            date,
            destination,
          }}
          components={{ 1: <span className={destinationClassName} /> }}
        />
      );
    case SliceDirection.Return:
      return (
        <Trans
          i18nKey={"core-flights.itinerary.card.slice.returnTitle"}
          values={{
            date,
            destination,
          }}
          components={{
            1: <span className={destinationClassName} />,
          }}
        />
      );
  }
};

const FlightSegment = ({
  slice,
  nextSegment,
  scheduleChangeDisplayMode,
  scheduleChangeSeverity,
  segment,
  updatedSegment,
}: {
  slice: TripSlice;
  nextSegment?: FlightItinerarySegment;
  scheduleChangeDisplayMode: ScheduleChangeDisplayMode;
  scheduleChangeSeverity?: ScheduleChangeSeverity;
  segment: FlightItinerarySegment;
  updatedSegment?: FlightItinerarySegment;
}) => {
  const airlines = useSelector(getAirlines);
  const airports = useSelector(getAirports);
  return (
    <FlightDetailsSegment
      cabinClass={segment.cabinClassName}
      nextSegment={
        nextSegment != null
          ? transformToTripSegment(nextSegment, airlines, airports)
          : undefined
      }
      flightSlice={slice}
      planeInfo={undefined}
      scheduleChangeDisplayMode={scheduleChangeDisplayMode}
      scheduleChangeSeverity={scheduleChangeSeverity}
      segment={transformToTripSegment(segment, airlines, airports)}
      updatedSegment={updatedSegment}
    />
  );
};

const transformToTripSlice = (
  slice: AirBookingFlightItinerarySlice,
  isOutgoing: boolean,
  airlines: AirlineMap,
  airports: AirportMap
): TripSlice => {
  const firstSegment = slice.segments[0];
  const lastSegment = slice.segments[slice.segments.length - 1];
  const originCode = firstSegment.origin.locationCode;
  const destinationCode = lastSegment.destination.locationCode;

  const originName = airports?.[originCode]?.name ?? originCode;
  const destinationName = airports?.[destinationCode]?.name ?? destinationCode;

  const zonedDepartureTime =
    firstSegment.zonedUpdatedDeparture ??
    firstSegment.zonedScheduledDeparture ??
    "";
  const zonedArrivalTime =
    lastSegment.zonedUpdatedArrival ?? lastSegment.zonedScheduledArrival ?? "";
  const totalDurationMinutes = dayjs(zonedArrivalTime).diff(
    dayjs(zonedDepartureTime),
    "minutes"
  );

  return {
    departureTime:
      firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture,
    zonedDepartureTime,

    arrivalTime: lastSegment.updatedArrival ?? lastSegment.scheduledArrival,
    outgoing: isOutgoing,
    destinationName,
    originCode,
    id: "",
    segmentDetails: slice.segments.map((segment) =>
      transformToTripSegment(segment, airlines, airports)
    ),
    originName,
    totalDurationMinutes,
    destinationCode,
    stops: slice.segments.reduce((acc, segment) => acc + segment.stops, 0),
  };
};

const transformToTripSegment = (
  segment: FlightItinerarySegment,
  airlines: AirlineMap,
  airports: AirportMap
): TripSegment => {
  const marketingAirline = {
    name:
      airlines?.[segment.marketingAirline.code]?.displayName ??
      segment.marketingAirline.code,
    code: segment.marketingAirline.code,
  };

  const operatingAirline =
    segment.operatingAirline != null
      ? {
          name:
            airlines?.[segment.operatingAirline.code]?.displayName ??
            segment.operatingAirline.code,
          code: segment.operatingAirline.code,
        }
      : marketingAirline;

  return {
    flightNumber: `${segment.marketingAirline.flightNumber}`,
    airlineCode: marketingAirline.code,
    airlineName: marketingAirline.name,
    marketingAirline,
    operatingAirline,
    codeSharedCarrier: segment.codeSharedCarrier,
    originName:
      airports?.[segment.origin.locationCode]?.name ??
      segment.origin.locationCode,
    originCode: segment.origin.locationCode,
    destinationName:
      airports?.[segment.destination.locationCode]?.name ??
      segment.destination.locationCode,
    destinationCode: segment.destination.locationCode,
    departureTime: segment.updatedDeparture ?? segment.scheduledDeparture,
    zonedDepartureTime:
      segment.zonedUpdatedDeparture ?? segment.zonedScheduledDeparture ?? "",
    zonedArrivalTime:
      segment.zonedUpdatedArrival ?? segment.zonedScheduledArrival ?? "",
    arrivalTime: segment.updatedArrival ?? segment.scheduledArrival,
    stops: segment.stops,
    plusDays: segment.plusDays,
    stopoverDurationMinutes: segment.stopoverDurationMinutes,
    isSelfTransferLayover: segment.isSelfTransferLayover,
  };
};
